<template>
  <div
    class="flex justify-between flex-col h-full m-0 flex-1 bg-white dark:bg-slate-900"
  >
    <settings-header
      button-route="new"
      :header-title="contact.name"
      show-back-button
      :back-button-label="$t('CONTACT_PROFILE.BACK_BUTTON')"
      :back-url="backUrl"
      :pipeline-contact="pipelineContact"
      :show-new-button="false"
    >
      <thumbnail
        v-if="contact.thumbnail"
        :src="contact.thumbnail"
        :username="contact.name"
        size="32px"
        class="mr-2 rtl:mr-0 rtl:ml-2"
      />
    </settings-header>

    <div
      v-if="uiFlags.isFetchingItem && !contact.id"
      class="text-center p-4 text-base h-full"
    >
      <spinner size="" />
      <span>{{ $t('CONTACT_PROFILE.LOADING') }}</span>
    </div>
    <div
      v-else-if="contact.id"
      class="overflow-hidden flex-1 min-w-0 flex flex-col"
    >
      <div class="flex flex-col sm:flex-row flex-1 min-h-0 w-full max-w-full">
        <contact-info-panel
          :show-close-button="false"
          :show-avatar="true"
          :contact="contact"
          :pipeline-contact="pipelineContact"
        />
        <div
          class="flex-1 min-w-0 w-full sm:w-auto h-full flex flex-col min-h-0"
        >
          <div class="contact-profile-tabs shrink-0">
            <woot-tabs :index="selectedTabIndex" @change="onClickTabChange">
              <woot-tabs-item
                v-for="tab in profileTabs"
                :key="tab.key"
                :name="tab.name"
                :show-badge="false"
              />
            </woot-tabs>
          </div>
          <div
            class="flex min-h-0 flex-1 flex-col overflow-hidden bg-slate-25 dark:bg-slate-800"
          >
            <div
              v-show="selectedTabIndex === 0"
              class="flex min-h-0 flex-1 flex-col overflow-hidden"
            >
              <div
                v-if="conversationId && inboxId"
                class="flex min-h-0 flex-1 flex-col overflow-hidden"
              >
                <conversation-box
                  :inbox-id="inboxId"
                  :is-contact-panel-open="isContactPanelOpen"
                  :is-on-expanded-layout="isOnExpandedLayout"
                  :profile-contact="contactForEmbeddedConversation"
                  :use-fixed-contact-panel="true"
                  @contact-panel-toggle="onToggleContactPanel"
                />
              </div>
              <div
                v-else-if="!isResolvingConversation"
                class="flex min-h-0 flex-1 items-center justify-center p-6 text-center text-slate-500 dark:text-slate-400"
              >
                {{ $t('CONTACT_PANEL.CONVERSATIONS.NO_RECORDS_FOUND') }}
              </div>
              <div
                v-else
                class="flex min-h-0 flex-1 items-center justify-center"
              >
                <spinner size="" />
              </div>
            </div>
            <contact-notes
              v-show="selectedTabIndex === 1"
              :contact-id="Number(contactId)"
              class="h-full min-h-0 overflow-auto p-4"
              @note-content-changed="unsavedNoteContent = $event"
            />
          </div>
        </div>
      </div>
    </div>
    <woot-delete-modal
      v-if="showUnsavedModal"
      :show.sync="showUnsavedModal"
      :on-close="cancelLeave"
      :on-confirm="confirmLeave"
      :title="$t('NOTES.CONFIRMATION.UNSAVED_CHANGES_TITLE')"
      :message="$t('NOTES.CONFIRMATION.UNSAVED_CHANGES_MESSAGE')"
      :confirm-text="$t('NOTES.CONFIRMATION.LEAVE')"
      :reject-text="$t('NOTES.CONFIRMATION.CANCEL')"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ContactInfoPanel from '../components/ContactInfoPanel.vue';
import ContactNotes from 'dashboard/modules/notes/NotesOnContactPage';
import ConversationBox from '../../../../components/widgets/conversation/ConversationBox.vue';
import SettingsHeader from '../../settings/SettingsHeader.vue';
import Spinner from 'shared/components/Spinner.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';

export default {
  components: {
    ContactInfoPanel,
    ContactNotes,
    ConversationBox,
    SettingsHeader,
    Spinner,
    Thumbnail,
  },
  mixins: [uiSettingsMixin],
  props: {
    contactId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      selectedTabIndex: 0,
      unsavedNoteContent: '',
      showUnsavedModal: false,
      nextRoute: null,
      conversationId: null,
      inboxId: null,
      isResolvingConversation: true,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      currentChat: 'getSelectedChat',
      chatList: 'getAllConversations',
    }),
    profileTabs() {
      return [
        {
          key: 'conversation',
          name: this.$t('CONTACT_PROFILE.CONVERSATION_TAB'),
        },
        { key: 'notes', name: this.$t('NOTES.HEADER.TITLE') },
      ];
    },
    contact() {
      return this.$store.getters['contacts/getContact'](this.contactId);
    },
    /** Passed into embedded ConversationBox so sidebar/macros use this contact id (not only meta.sender). */
    contactForEmbeddedConversation() {
      return this.contact?.id ? this.contact : null;
    },
    pipelineContact() {
      return this.$route?.query?.pipelineContact === 'true';
    },
    backUrl() {
      return this.pipelineContact
        ? `/app/accounts/${this.$route.params.accountId}/pipelines`
        : `/app/accounts/${this.$route.params.accountId}/contacts`;
    },
    isContactPanelOpen() {
      if (this.currentChat.id) {
        const {
          is_contact_sidebar_open: isContactSidebarOpen,
        } = this.uiSettings;
        return isContactSidebarOpen;
      }
      return false;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const {
        conversation_display_type: conversationDisplayType = CONDENSED,
      } = this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
  },
  watch: {
    contactId() {
      this.selectedTabIndex = 0;
      this.unsavedNoteContent = '';
      this.conversationId = null;
      this.inboxId = null;
      this.isResolvingConversation = true;
      this.fetchContactDetails().then(() => this.resolveInitialConversation());
    },
  },
  async mounted() {
    this.$store.dispatch('agents/get');
    await this.fetchContactDetails();
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
    this.resetContactPanelState();
    if (typeof window !== 'undefined' && window.bus) {
      window.bus.$on('open-conversation', this.handleConversationSelected);
    }
    await this.resolveInitialConversation();
  },
  beforeDestroy() {
    if (typeof window !== 'undefined' && window.bus) {
      window.bus.$off('open-conversation', this.handleConversationSelected);
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.unsavedNoteContent.trim()) {
      this.showUnsavedModal = true;
      this.nextRoute = next;
    } else {
      next();
    }
  },
  methods: {
    onClickTabChange(index) {
      this.selectedTabIndex = index;
    },
    fetchContactDetails() {
      const { contactId: id } = this;
      return this.$store.dispatch('contacts/show', { id });
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    findConversation() {
      if (!this.conversationId) {
        return undefined;
      }
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(
        c => Number(c.id) === Number(conversationId)
      );
      return chat;
    },
    async fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      let chat = this.findConversation();
      if (!chat) {
        await this.$store.dispatch('getConversation', this.conversationId);
        chat =
          this.findConversation() ||
          this.$store.getters.getConversationById(
            parseInt(this.conversationId, 10)
          );
      }
      if (chat) {
        if (this.inboxId != null && this.inboxId !== '') {
          await this.$store.dispatch('setActiveInbox', this.inboxId);
        }
        await this.setActiveChat();
      }
    },
    async setActiveChat() {
      if (!this.conversationId) {
        return;
      }
      const messageId = this.$route.query.messageId;
      let selectedConversation = this.findConversation();
      if (!selectedConversation) {
        selectedConversation = this.$store.getters.getConversationById(
          parseInt(this.conversationId, 10)
        );
      }
      if (!selectedConversation) {
        return;
      }
      if (
        this.currentChat.id &&
        selectedConversation.id === this.currentChat.id
      ) {
        return;
      }
      await this.$store.dispatch('setActiveChat', {
        data: selectedConversation,
        after: messageId,
      });
      if (messageId && typeof window !== 'undefined' && window.bus) {
        window.bus.$emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
      }
    },
    resetContactPanelState() {
      this.updateUISettings({
        is_contact_sidebar_open: false,
      });
    },
    onToggleContactPanel() {
      this.updateUISettings({
        is_contact_sidebar_open: !this.isContactPanelOpen,
      });
    },
    handleConversationSelected(conversation) {
      if (!conversation?.id) {
        return;
      }
      this.conversationId = String(conversation.id);
      this.inboxId = conversation.inbox_id;
      this.selectedTabIndex = 0;
      this.fetchConversationIfUnavailable();
    },
    handleNoteAdd(content) {
      this.unsavedNoteContent = '';
      this.$store.dispatch('contactNotes/create', {
        contactId: this.contactId,
        content,
      });
    },
    cancelLeave() {
      this.showUnsavedModal = false;
      this.nextRoute = null;
    },
    confirmLeave() {
      if (this.unsavedNoteContent.trim()) {
        this.handleNoteAdd(this.unsavedNoteContent);
      }
      this.unsavedNoteContent = '';
      this.showUnsavedModal = false;
      const next = this.nextRoute;
      this.nextRoute = null;
      if (next) {
        next();
      }
    },
    async resolveInitialConversation() {
      this.isResolvingConversation = true;
      try {
        const qId = this.$route.query?.conversation_id;
        if (qId) {
          this.conversationId = String(qId);
          await this.$store.dispatch('getConversation', this.conversationId);
          const chat = this.$store.getters.getConversationById(
            parseInt(this.conversationId, 10)
          );
          if (chat?.inbox_id != null) {
            this.inboxId = chat.inbox_id;
          }
          await this.fetchConversationIfUnavailable();
          return;
        }

        await this.$store.dispatch(
          'fetchContactConversations',
          Number(this.contactId)
        );
        const list =
          this.$store.getters.getConversationsForContact(
            Number(this.contactId)
          ) || [];
        const sorted = [...list].sort(
          (a, b) => (b.timestamp || 0) - (a.timestamp || 0)
        );
        const first = sorted[0];
        if (!first) {
          this.conversationId = null;
          this.inboxId = null;
          return;
        }
        this.conversationId = String(first.id);
        this.inboxId = first.inbox_id;
        await this.fetchConversationIfUnavailable();
      } finally {
        this.isResolvingConversation = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* Profile-only: brand blue for active tab (rgb(31, 147, 255) / #1f93ff — same as $color-woot). */
.contact-profile-tabs {
  :deep(.tabs-title.is-active a) {
    border-color: rgb(31, 147, 255) !important;
    color: rgb(31, 147, 255) !important;
  }

  :deep(.tabs-title.is-active .badge) {
    background-color: rgb(31, 147, 255) !important;
    color: #fff !important;
  }
}
</style>
